import { ClubVideoTag } from '@customTypes/types';
import { useForm } from '@mantine/form';
import { useDebouncedCallback } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import type { AxiosProgressEvent } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import {
  useClubProfileVideoListQuery,
  useDeleteVideoItemMutation,
  useUploadVideoMutation
} from '@/shared/api/requests';
import { APP_CONST } from '@/shared/constants';

type ClubVideoForm = {
  ClubTour: {
    file: File | null | string;
    title: string;
    description: string;
    tag: ClubVideoTag;
    id: null | string;
    thumbnailPath: null | string;
    ClubProfileId: string;
    IsProfile: boolean;
  };
  EventVideo: {
    file: File | null | string;
    title: string;
    description: string;
    thumbnailPath: null | string;
    tag: ClubVideoTag;
    id: null | string;
    ClubProfileId: string;
    IsProfile: boolean;
  };
  WorkingHours: {
    file: File | null | string;
    title: string;
    id: null | string;
    thumbnailPath: null | string;
    description: string;
    tag: ClubVideoTag;
    ClubProfileId: string;
    IsProfile: boolean;
  };
};

export type ClubVideoFormKey = keyof ClubVideoForm;

export const useVideoPage = () => {
  const { formatMessage } = useIntl();
  const { id: clubId } = useParams({ from: '/clubOwner/$id' });
  const navigate = useNavigate({ from: '/clubOwner/$id/onboard/$step' });
  const queryClient = useQueryClient();
  const uploadVideoMutation = useUploadVideoMutation();
  const deleteVideoMutation = useDeleteVideoItemMutation();
  const profileVideoList = useClubProfileVideoListQuery({ profileId: clubId });

  const queries = [profileVideoList];

  const isLoading = queries.some((q) => q.isLoading);

  const progressRef = useRef<Record<string, number>>({});
  const [progress, setProgress] = useState(0);

  const debouncedUpdateCommonProgress = useDebouncedCallback(() => {
    const values = Object.values(progressRef.current);
    const commonProgress = values.length
      ? Math.round(values.reduce((sum, val) => sum + val, 0) / values.length)
      : 0;
    setProgress(commonProgress);
  }, 100);

  const form = useForm<ClubVideoForm>({
    initialValues: {
      ClubTour: {
        file: null,
        title: formatMessage({
          id: 'pages.onboarding.club.video.options.ClubTour'
        }),
        description: '',
        tag: ClubVideoTag.ClubTour,
        ClubProfileId: '',
        IsProfile: false,
        id: null,
        thumbnailPath: null
      },
      EventVideo: {
        file: null,
        title: formatMessage({
          id: 'pages.onboarding.club.video.options.EventVideo'
        }),
        description: '',
        tag: ClubVideoTag.EventVideo,
        ClubProfileId: '',
        IsProfile: false,
        id: null,
        thumbnailPath: null
      },
      WorkingHours: {
        file: null,
        title: formatMessage({
          id: 'pages.onboarding.club.video.options.WorkingHours'
        }),
        description: '',
        tag: ClubVideoTag.WorkingHours,
        ClubProfileId: '',
        IsProfile: false,
        id: null,
        thumbnailPath: null
      }
    }
  });

  const next = () => {
    navigate({
      to: '/clubOwner/$id/profile'
    });
  };

  useEffect(() => {
    if (profileVideoList.isSuccess && profileVideoList.data?.data.length) {
      Object.entries(form.getValues()).forEach(([key, value]) => {
        const currentItem = profileVideoList.data.data.filter(
          (item) => item.tag === value.tag
        );
        if (currentItem.length) {
          form.setFieldValue(`${key}.file`, currentItem[0].filePath);
          form.setFieldValue(`${key}.id`, currentItem[0].id);
          form.setFieldValue(
            `${key}.thumbnailPath`,
            currentItem[0].thumbnailPath
          );
        }
      });
    }
  }, [profileVideoList.data?.data]);

  const isValidForm =
    !!form.getValues().ClubTour.file &&
    !!form.getValues().EventVideo.file &&
    !!form.getValues().WorkingHours.file;

  const onDeleteVideo = useCallback(
    async (videoId: string | null, tag: ClubVideoFormKey) => {
      if (videoId) {
        await deleteVideoMutation.mutateAsync(
          {
            params: {
              videoId
            }
          },
          {
            onSuccess: () => {
              form.setFieldValue(`${tag}.file`, null);
              form.setFieldValue(`${tag}.id`, null);
              queryClient.invalidateQueries({
                queryKey: ['ClubProfileVideoList', clubId]
              });
            }
          }
        );
      }
    },
    []
  );

  const updateProgress = (key: string, loaded: number, total: number) => {
    const newPercentage = Math.round((loaded * 100) / total);
    progressRef.current[key] = newPercentage;
    debouncedUpdateCommonProgress();
  };

  const onSubmit = form.onSubmit(async (values) => {
    form.setSubmitting(true);

    localStorage.setItem(
      APP_CONST.LOCAL_STORAGE.IS_FIRST_LOGIN,
      JSON.stringify(false)
    );

    try {
      for (const [key, value] of Object.entries(values)) {
        if (typeof value.file !== 'string' && value.file !== null) {
          const formData = new FormData();

          formData.append('File', value.file);
          formData.append('Title', value.title);
          formData.append('Description', value.description);
          formData.append('Tag', value.tag);
          formData.append('ClubProfileId', clubId);

          // Aștepți upload-ul înainte să treci la următorul
          await uploadVideoMutation.mutateAsync(
            {
              params: {
                formData
              },
              config: {
                onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                  if (progressEvent && progressEvent.total) {
                    updateProgress(
                      key,
                      progressEvent.loaded,
                      progressEvent.total
                    );
                  }
                }
              }
            },
            {
              onSuccess: () => {
                notifications.show({
                  title: formatMessage({
                    id: 'notifications.successDataUpdate'
                  }),
                  ...notificationStyleConfig
                });
              },
              onError: () => {
                notifications.show({
                  title: formatMessage({
                    id: 'notifications.errorOnDataUpdate'
                  }),
                  ...notificationStyleConfig
                });
              }
            }
          );

          // Ștergi video-ul vechi doar după ce upload-ul a reușit
          if (value.id !== null) {
            await deleteVideoMutation.mutateAsync({
              params: {
                videoId: value.id.toString()
              }
            });
          }
        }
      }

      queryClient.invalidateQueries({
        queryKey: ['ClubProfileVideoList', clubId]
      });
      next();
    } finally {
      form.setSubmitting(false);
    }
  });

  return {
    state: {
      isLoading,
      form,
      isValidForm,
      progress
    },
    functions: { onSubmit, onDeleteVideo }
  };
};
